import React, { useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTheme } from "../contexts/ThemeSelection";
import { useUser } from "../contexts/UserContext";
import { ALL_NAV_ITEMS, useAvailableNavItems } from "../hooks/useNavItems";
import useFocusMode from "../hooks/useFocusMode";
import usePregnancy from "../contexts/PregnancyContext";
import useCollectionBadge from "../hooks/useCollectionBadge";
import NotificationBell from "./NotificationBell";
import styles from "../styles/DesktopSidebar.module.css";

const DesktopSidebar: React.FC = () => {
  const { activeTheme, theme } = useTheme();
  const userContext = useUser();
  const availableItems = useAvailableNavItems();
  const { focusModeEnabled, profile } = useFocusMode();
  const { isPregnant } = usePregnancy();
  const location = useLocation();
  const { count: collectionBadge } = useCollectionBadge();

  const linkStyle: React.CSSProperties = { color: theme[activeTheme]?.color };
  const accentColor = theme[activeTheme].todayColor || theme[activeTheme].altColor || theme[activeTheme].secondaryColor || theme[activeTheme].color;

  /** Check if a focus-mode tab path (with ?tab= query) matches the current location */
  const isTabActive = (path: string): boolean => {
    const [pathname, search] = path.split("?");
    if (location.pathname !== pathname) return false;
    if (!search) return true;
    const params = new URLSearchParams(search);
    const tabParam = params.get("tab");
    if (!tabParam) return true;
    const currentParams = new URLSearchParams(location.search);
    const currentTab = currentParams.get("tab");
    if (!currentTab) {
      const samePageTabs = profile?.tabNav.filter(t => t.path.split("?")[0] === pathname) || [];
      if (samePageTabs.length > 0) {
        const firstTabParams = new URLSearchParams(samePageTabs[0].path.split("?")[1] || "");
        return tabParam === firstTabParams.get("tab");
      }
    }
    return currentTab === tabParam;
  };

  // Standard mode: all available nav items
  const standardItems = useMemo(() => {
    const items: { to: string; icon: string; label: string }[] = [
      { to: "/", icon: "fa-solid fa-house", label: "Today" },
    ];

    ALL_NAV_ITEMS
      .filter((item) => availableItems.has(item.id))
      .forEach((item) => {
        const label = isPregnant && item.id === "womens-health" ? "Baby" : item.label;
        const icon = isPregnant && item.id === "womens-health" ? "fa-solid fa-baby" : item.icon;
        items.push({ to: item.path, icon, label });
      });

    if (userContext?.userDetails?.admin) {
      items.push({ to: "/admin", icon: "fa-solid fa-shield-halved", label: "Admin" });
    }

    return items;
  }, [availableItems, userContext?.userDetails?.admin, isPregnant]);

  // Focus mode: profile tabs + extra overflow items (Account, Community, etc.)
  const focusItems = useMemo(() => {
    if (!profile) return [];
    return profile.tabNav.map((tab) => ({
      to: tab.path,
      icon: isPregnant && tab.id === "womens__cycle" ? "fa-solid fa-baby" : tab.icon,
      label: isPregnant && tab.id === "womens__cycle" ? "Pregnancy" : tab.label,
      id: tab.id,
    }));
  }, [profile, isPregnant]);

  const isFocus = focusModeEnabled && profile;

  return (
    <div className={styles.sidebar}>
      <div
        className={styles.panel}
        style={{
          backgroundColor: theme[activeTheme].panelColor,
          color: theme[activeTheme].color,
        }}
      >
        <div
          className={styles.panelHeader}
          style={{ background: theme[activeTheme].heroGradient }}
        >
          <div className={styles.panelBrand}>
            <img
              src={`${process.env.PUBLIC_URL}/icons/icon-96x96.png`}
              alt="OnyxLife"
              className={styles.panelLogo}
            />
            <span className={styles.panelTitle}>
              {isFocus ? profile.label : "OnyxLife"}
            </span>
          </div>
        </div>

        <nav className={styles.navItems}>
          {isFocus ? (
            <>
              {focusItems.map((item) => {
                const active = isTabActive(item.to);
                return (
                  <NavLink
                    key={item.id}
                    to={item.to}
                    className={`${styles.menuItem} ${active ? styles.menuItemActive : ""}`}
                    style={active ? { color: accentColor } : linkStyle}
                    end={item.to === "/"}
                  >
                    <span className={styles.menuItemIcon}>
                      <i className={item.icon} />
                    </span>
                    <span className={styles.menuItemLabel}>{item.label}</span>
                  </NavLink>
                );
              })}
              {/* Divider between focus tabs and overflow items */}
              <div className={styles.sidebarDivider} />
              <NavLink
                to="/community"
                className={({ isActive }) =>
                  `${styles.menuItem} ${isActive ? styles.menuItemActive : ""}`
                }
                style={linkStyle}
              >
                <span className={styles.menuItemIcon}>
                  <i className="fa-solid fa-users" />
                </span>
                <span className={styles.menuItemLabel}>Community</span>
              </NavLink>
              <NavLink
                to="/account"
                className={({ isActive }) =>
                  `${styles.menuItem} ${isActive ? styles.menuItemActive : ""}`
                }
                style={linkStyle}
              >
                <span className={styles.menuItemIcon}>
                  <i className="fa-solid fa-gear" />
                </span>
                <span className={styles.menuItemLabel}>Account</span>
              </NavLink>
              {userContext?.userDetails?.admin && (
                <NavLink
                  to="/admin"
                  className={({ isActive }) =>
                    `${styles.menuItem} ${isActive ? styles.menuItemActive : ""}`
                  }
                  style={linkStyle}
                >
                  <span className={styles.menuItemIcon}>
                    <i className="fa-solid fa-shield-halved" />
                  </span>
                  <span className={styles.menuItemLabel}>Admin</span>
                </NavLink>
              )}
            </>
          ) : (
            standardItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `${styles.menuItem} ${isActive ? styles.menuItemActive : ""}`
                }
                style={linkStyle}
                end={item.to === "/"}
              >
                <span className={styles.menuItemIcon}>
                  <i className={item.icon} />
                </span>
                <span className={styles.menuItemLabel}>{item.label}</span>
                {item.to === "/collection" && collectionBadge > 0 && (
                  <span className={styles.navBadge}>{collectionBadge > 9 ? "9+" : collectionBadge}</span>
                )}
              </NavLink>
            ))
          )}
        </nav>

        <div className={styles.sidebarFooter}>
          <NotificationBell />
        </div>
      </div>
    </div>
  );
};

export default DesktopSidebar;
